/**
 * RP-initiated logout URL for Hydra's `/oauth2/sessions/logout`.
 *
 * The id-token cookie is often gone by the time a logout page runs — the
 * expired-link restart ladder lands users on logout flows they never signed
 * in to on this browser, and session-conflict logs out on a sister domain
 * that never held the cookie. Interpolating that miss produced the literal
 * string `id_token_hint=undefined`, which Hydra rejects with
 * `invalid_request … go-jose: compact JWS format must have three parts` and
 * strands the user on the auth error page.
 *
 * Hydra also rejects `post_logout_redirect_uri` when `id_token_hint` is
 * missing (`Logout failed because query parameter post_logout_redirect_uri
 * is set but id_token_hint is missing`) and, identically, `state` when
 * `id_token_hint` is missing (`Logout failed because query parameter state
 * is set but id_token_hint is missing`). Since `state` is only echoed back
 * on `post_logout_redirect_uri` — which is itself dropped without a token —
 * it has no purpose in the token-less case. So without a token we send NO
 * query parameters at all: Hydra creates a logout challenge, walks it
 * through the auth app's `/logout` provider page — the Kratos and Hydra
 * sessions are still terminated — and finishes at Hydra's configured
 * default post-logout page instead of `postLogoutRedirectUri`.
 */
/**
 * A forwardable `id_token_hint` must at least look like a JWT (three
 * dot-separated parts). Clients running stale pre-fix bundles still send the
 * literal string "undefined" (or a corrupt token) — anything else forwarded
 * to Hydra 400s with a go-jose parse error and strands the user on the auth
 * error page, so treat implausible hints exactly like absent ones.
 */
export const isPlausibleIdTokenHint = (hint?: string | null): boolean => {
  if (typeof hint !== 'string' || !hint) {
    return false
  }

  return hint.split('.').filter(Boolean).length === 3
}

export const buildHydraLogoutUrl = ({
  hydraUrl,
  idToken,
  postLogoutRedirectUri,
  state
}: {
  hydraUrl: string
  idToken?: string
  postLogoutRedirectUri?: string
  state: string
}): string => {
  const params: string[] = []

  if (isPlausibleIdTokenHint(idToken)) {
    params.push(`id_token_hint=${idToken}`)
    if (postLogoutRedirectUri) {
      params.push(
        `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`
      )
    }
    params.push(`state=${state}`)
  }

  const logoutUrl = `${hydraUrl}/oauth2/sessions/logout`

  return params.length ? `${logoutUrl}?${params.join('&')}` : logoutUrl
}
